/**
 * New Analytics Service
 *
 * This module provides analytics tracking using the @upgather/analytics SDK.
 * It wraps the SDK with environment-specific configuration and provides
 * easy-to-use tracking functions.
 *
 * @module newAnalytics
 */

import {
  AnalyticsClient,
  AnalyticsClientFactory,
  MetricType,
} from '@upgather/analytics';
import { getCurrentUser } from 'aws-amplify/auth';
import { utmCodes } from '../store/useUtmCodeStore';

/* Environment configuration */
const eventId = process.env.GATSBY_EVENT_ID || '';
const envOrganizationId = process.env.GATSBY_ORGANIZATION_ID || '';
const apiEndpoint = process.env.GATSBY_ANALYTICS_SERVICE_ENDPOINT || '';

/**
 * Organization document ID from Strapi, set at runtime via EventProvider.
 * This is the actual Strapi document ID (e.g. "g3snzn07i5i53abeyjvscc5l")
 * required by the analytics service as the DynamoDB partition key.
 * Falls back to GATSBY_ORGANIZATION_ID env var if not set.
 */
let orgDocumentId: string | null = null;

export const setOrgDocumentId = (id: string) => {
  orgDocumentId = id;
};

const getOrganizationId = (): string => orgDocumentId || envOrganizationId;

/* Cached client instance */
let analyticsClient: AnalyticsClient | null = null;

/**
 * Get or create the analytics client singleton.
 * Lazily initializes the client on first use.
 */
const getClient = (): AnalyticsClient => {
  if (!analyticsClient) {
    analyticsClient = AnalyticsClientFactory.createClient({
      baseUrl: apiEndpoint,
      defaultEventId: eventId,
      validateEvents: false,
    });
  }
  return analyticsClient;
};

/**
 * Get the current user's username if authenticated.
 * Returns empty string if not authenticated.
 */
const getUsername = async (): Promise<string> => {
  try {
    const user = await getCurrentUser();
    return user.username || '';
  } catch {
    return '';
  }
};

/**
 * Track a metric event using the new analytics SDK.
 *
 * @param metricType - The type of metric to track
 * @param payload - Additional payload data (url is required)
 *
 * @example
 * ```typescript
 * await trackMetricNew({
 *   metricType: MetricType.pageview,
 *   payload: { url: window.location.href }
 * });
 * ```
 */
export const trackMetricNew = async ({
  metricType,
  payload,
}: {
  metricType: MetricType | string;
  payload: { url: string; [key: string]: unknown };
}): Promise<void> => {
  const client = getClient();
  const rawUtm = utmCodes();
  const username = await getUsername();

  /* Map href → url for analytics service compatibility.
     Many Gatsby call sites use 'href' for the page URL, but the
     IngestEventDto only declares 'url'. Without this mapping,
     href is silently stripped by the NestJS whitelist. */
  const normalizedPayload = (payload.href && !payload.url)
    ? { ...payload, url: payload.href as string }
    : { ...payload };

  /* Normalize UTM keys to camelCase for IngestEventDto compatibility.
     Marketing URLs may use snake_case (utm_source) or camelCase (utmSource).
     The Zustand UTM store preserves raw query param keys from qs.parse().
     The DTO expects camelCase — snake_case keys would be silently stripped. */
  const utmKeyMap: Record<string, string> = {
    utm_source: 'utmSource',
    utm_medium: 'utmMedium',
    utm_campaign: 'utmCampaign',
    utm_term: 'utmTerm',
    utm_content: 'utmContent',
  };
  const utm: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawUtm)) {
    utm[utmKeyMap[key] || key] = value;
  }

  try {
    await client.trackEvent({
      eventId,
      organizationId: getOrganizationId(),
      metricType: metricType as MetricType,
      payload: {
        ...normalizedPayload,
        /* Set userName only if not already provided by the caller's enhancedPayload.
           Registration events send userName as a display name (e.g. "John Doe")
           via enhancedPayload — we must not overwrite that with the Cognito email.
           For logins/pageviews/interactions, userName is absent from the payload,
           so we fall back to the Cognito username (email). */
        ...('userName' in normalizedPayload ? {} : { userName: username }),
        ...utm,
      },
    });
  } catch (error) {
    console.error('[newAnalytics] Failed to track metric:', error);
  }
};

/**
 * Track a pageview event.
 *
 * @param url - The URL of the page being viewed
 *
 * @example
 * ```typescript
 * await trackPageviewNew(window.location.href);
 * ```
 */
export const trackPageviewNew = async (url: string): Promise<void> => {
  await trackMetricNew({
    metricType: MetricType.pageview,
    payload: { url },
  });
};

/**
 * Track a login event.
 *
 * @param email - The email of the user logging in
 *
 * @example
 * ```typescript
 * await trackLoginNew('user@example.com');
 * ```
 */
export const trackLoginNew = async (email: string): Promise<void> => {
  await trackMetricNew({
    metricType: MetricType.login,
    payload: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      email,
      eventId,
    },
  });
};

/**
 * Track a registration event.
 *
 * @param email - The email of the user registering
 * @param additionalData - Any additional registration data
 *
 * @example
 * ```typescript
 * await trackRegistrationNew('user@example.com', { plan: 'premium' });
 * ```
 */
export const trackRegistrationNew = async (
  email: string,
  additionalData?: Record<string, unknown>
): Promise<void> => {
  await trackMetricNew({
    metricType: MetricType.registration,
    payload: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      email,
      eventId,
      ...additionalData,
    },
  });
};

/**
 * Track an interaction event (surveys, polls, UI interactions).
 *
 * @param interactionType - The type of interaction
 * @param additionalData - Any additional interaction data
 *
 * @example
 * ```typescript
 * await trackInteractionNew('survey_complete', { surveyId: '123' });
 * ```
 */
export const trackInteractionNew = async (
  interactionType: string,
  additionalData?: Record<string, unknown>
): Promise<void> => {
  await trackMetricNew({
    metricType: MetricType.interaction,
    payload: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      interactionType,
      ...additionalData,
    },
  });
};

/* Re-export MetricType for convenience */
export { MetricType };
